<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ campaign?.campaignName || '行銷活動' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="$router.push({ name: 'MarketingCampaignList' })">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </button>
        <button
          v-if="canShowSaveButton"
          type="button"
          class="btn btn-primary"
          :disabled="saving"
          @click="handleSave"
        >
          <i class="ri-save-line"></i>
          {{ saving ? '儲存中...' : '儲存' }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
    </div>

    <template v-else-if="campaign">
      <div class="meta-card">
        <div><strong>代碼：</strong>{{ campaign.campaignCode }}</div>
        <div><strong>狀態：</strong>{{ statusLabel(campaign.status) }}</div>
        <div><strong>追蹤碼：</strong>{{ campaign.trackingCode || '-' }}</div>
        <div v-if="campaign.playbookCode"><strong>劇本：</strong>{{ playbookLabel(campaign.playbookCode) }}</div>
        <div><strong>期間：</strong>{{ formatPeriod(campaign.startAt, campaign.endAt) }}</div>
      </div>

      <div class="detail-tabs">
        <button
          v-for="tab in detailTabs"
          :key="tab.key"
          type="button"
          class="tab-btn"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          <i :class="tab.icon"></i>
          {{ tab.label }}
        </button>
      </div>

      <div v-show="activeTab === 'analytics'" class="tab-panel">
        <MarketingCampaignAnalyticsPanel
          :campaign-id="campaign.id"
          :campaign-status="campaign.status"
        />
      </div>

      <div v-show="activeTab === 'participants'" class="tab-panel">
        <MarketingCampaignParticipantsPanel
          :campaign-id="campaign.id"
          :campaign-status="campaign.status"
        />
      </div>

      <div v-show="activeTab === 'settings'" class="tab-panel">
          <div class="settings-card">
            <h3>活動設定</h3>

            <div v-if="canToggleStatus" class="status-switch-row">
              <div class="status-switch-text">
                <span class="status-switch-label">啟用活動</span>
                <small v-if="campaign.status === 'draft'">
                  關閉時儲存為草稿；開啟後按「儲存」即正式啟用。若流程尚未設定完整，儲存時會提示需補齊的項目。
                </small>
                <small v-else-if="campaign.status === 'active'">
                  目前運作中；關閉後按「儲存」即停用，不再觸發新參與
                </small>
                <small v-else-if="campaign.status === 'inactive'">
                  目前已停用；開啟後按「儲存」即重新啟用
                </small>
              </div>
              <label class="toggle-switch">
                <input
                  v-model="campaignEnabled"
                  type="checkbox"
                />
                <span class="slider"></span>
              </label>
            </div>

            <template v-if="isEditable">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">活動名稱</label>
                <input v-model="campaignForm.campaignName" type="text" class="form-control" />
              </div>
              <div class="form-group">
                <label class="form-label">預算上限（元，選填）</label>
                <input v-model.number="campaignForm.budgetCap" type="number" min="0" class="form-control" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">開始日期</label>
                <input v-model="campaignForm.startAtDate" type="date" class="form-control" />
              </div>
              <div class="form-group">
                <label class="form-label">結束日期</label>
                <input v-model="campaignForm.endAtDate" type="date" class="form-control" />
              </div>
            </div>
            <div class="form-section eligibility-section">
              <h4 class="section-title">參與對象</h4>
              <p class="eligibility-section-desc">
                決定誰可以進入此行銷活動流程；與各步驟內的條件篩選分開設定。
              </p>

              <div class="eligibility-mode-cards">
                <label
                  class="eligibility-mode-card"
                  :class="{ 'eligibility-mode-card--selected': eligibilityMode === 'friends' }"
                >
                  <input v-model="eligibilityMode" type="radio" value="friends" />
                  <i class="ri-user-heart-line eligibility-mode-icon"></i>
                  <div class="eligibility-mode-text">
                    <span>全部好友</span>
                    <small>LINE 官方帳號好友皆可參與（含尚未綁定會員者）</small>
                    <small class="eligibility-mode-action-hint">
                      流程動作僅支援 LOCKLINE 票券；點數與 Wixtar 票券不適用。
                    </small>
                  </div>
                </label>
                <label
                  class="eligibility-mode-card"
                  :class="{ 'eligibility-mode-card--selected': eligibilityMode === 'members' }"
                >
                  <input v-model="eligibilityMode" type="radio" value="members" />
                  <i class="ri-vip-crown-line eligibility-mode-icon"></i>
                  <div class="eligibility-mode-text">
                    <span>會員限定</span>
                    <small>僅已註冊會員可參與，可再依等級與標籤篩選</small>
                  </div>
                </label>
                <label
                  class="eligibility-mode-card"
                  :class="{ 'eligibility-mode-card--selected': eligibilityMode === 'rule' }"
                >
                  <input v-model="eligibilityMode" type="radio" value="rule" />
                  <i class="ri-filter-3-line eligibility-mode-icon"></i>
                  <div class="eligibility-mode-text">
                    <span>受眾規則</span>
                    <small>套用已建立的受眾規則（會員）</small>
                  </div>
                </label>
              </div>

              <div v-if="eligibilityMode === 'rule'" class="eligibility-mode-panel">
                <div class="form-group eligibility-panel-field">
                  <label class="form-label">選擇受眾規則</label>
                  <select
                    v-model="eligibilityAudienceRuleId"
                    class="form-control"
                    :disabled="audienceRulesLoading"
                  >
                    <option value="">請選擇受眾規則</option>
                    <option v-for="rule in audienceRules" :key="rule.id" :value="rule.id">
                      {{ rule.name }}
                      <template v-if="rule.audienceCount != null">
                        （{{ Number(rule.audienceCount).toLocaleString() }} 人）
                      </template>
                    </option>
                  </select>
                </div>
                <p v-if="audienceRulesLoading" class="form-hint">載入受眾規則中...</p>
                <p v-else-if="!audienceRules.length" class="eligibility-info-box">
                  尚無可用的受眾規則，請先
                  <router-link :to="{ name: 'ClientAudienceRuleCreate' }">建立受眾規則</router-link>。
                </p>
                <p v-else-if="selectedAudienceRule?.description" class="eligibility-info-box">
                  {{ selectedAudienceRule.description }}
                </p>
                <p class="eligibility-info-box eligibility-info-box--muted">
                  活動執行時會依規則即時判斷會員是否符合。可在
                  <router-link :to="{ name: 'ClientAudienceRules' }">受眾規則管理</router-link>
                  建立或調整規則。
                </p>
              </div>

              <div v-else-if="eligibilityMode === 'friends'" class="eligibility-mode-panel">
                <div class="eligibility-manual-block">
                  <TagSelectorMultiple
                    v-model="eligibilityTagIds"
                    label="再以標籤篩選（選填）"
                    hint="未選則不限制標籤；依 LINE 好友身上的標籤判斷"
                  />
                </div>
                <div class="eligibility-manual-block">
                  <TagMatchTypeToggle v-model="eligibilityTagLogic" label="標籤邏輯" />
                </div>
                <div class="eligibility-preview-card">
                  <div class="eligibility-preview-header">
                    <i class="ri-eye-line"></i>
                    <span>預覽好友人數</span>
                    <button
                      type="button"
                      class="btn btn-sm btn-secondary"
                      :disabled="eligibilityPreviewLoading"
                      title="重新計算"
                      @click="previewManualEligibility"
                    >
                      <i class="ri-refresh-line" :class="{ spinning: eligibilityPreviewLoading }"></i>
                    </button>
                  </div>
                  <div class="eligibility-preview-body">
                    <div v-if="eligibilityPreviewLoading" class="eligibility-preview-loading">
                      <div class="spinner-small"></div>
                      <span>計算中...</span>
                    </div>
                    <div v-else-if="manualEligibilityPreviewError" class="eligibility-preview-error">
                      <i class="ri-error-warning-line"></i>
                      <span>{{ manualEligibilityPreviewError }}</span>
                    </div>
                    <div v-else-if="manualEligibilityPreviewCount != null" class="eligibility-preview-result">
                      <div class="eligibility-preview-count">
                        <span class="count-value">{{ manualEligibilityPreviewCount.toLocaleString() }}</span>
                        <span class="count-label">人</span>
                      </div>
                      <p class="eligibility-preview-hint">
                        <i class="ri-information-line"></i>
                        <span v-if="manualEligibilityPreviewUnrestricted">
                          未設定標籤，所有 LINE 好友皆符合參與對象條件
                        </span>
                        <span v-else>
                          符合目前標籤條件的 LINE 好友人數
                        </span>
                      </p>
                    </div>
                    <div v-else class="eligibility-preview-placeholder">
                      <span>尚無預覽資料，請點上方重新整理計算</span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="eligibilityMode === 'members'" class="eligibility-mode-panel">
                <div class="eligibility-manual-block">
                  <TagSelectorMultiple
                    v-model="eligibilityTagIds"
                    label="再以標籤篩選（選填）"
                    hint="未選則不限制標籤；依會員標籤判斷"
                  />
                </div>
                <div class="eligibility-manual-block">
                  <TagMatchTypeToggle v-model="eligibilityTagLogic" label="標籤邏輯" />
                </div>
                <div class="eligibility-manual-block tier-filter-group">
                  <label class="form-label">會員等級（必選，預設全選）</label>
                  <div v-if="memberTiersLoading" class="form-hint">載入等級中...</div>
                  <div v-else-if="memberTiers.length === 0" class="form-hint">
                    尚無會員等級，請先至會員等級管理建立。
                  </div>
                  <div v-else class="tier-chip-group">
                    <button
                      v-for="tier in memberTiers"
                      :key="tier.id"
                      type="button"
                      class="tier-chip"
                      :class="{ 'tier-chip--active': isTierSelected(tier.id) }"
                      @click="toggleEligibilityTier(tier.id)"
                    >
                      <i
                        class="tier-chip-icon"
                        :class="isTierSelected(tier.id) ? 'ri-checkbox-circle-fill' : 'ri-circle-line'"
                      ></i>
                      {{ tier.tierName }}
                    </button>
                  </div>
                  <p class="form-hint">
                    請勾選可參與的會員等級；若要開放全部會員，請維持全選。
                    派點／派券將依客戶會員體系（{{ memberSystemType === 'WIXTAR' ? 'Wixtar' : 'LOCKLINE' }}）執行。
                  </p>
                </div>

                <div class="eligibility-preview-card">
                  <div class="eligibility-preview-header">
                    <i class="ri-eye-line"></i>
                    <span>預覽人數</span>
                    <button
                      type="button"
                      class="btn btn-sm btn-secondary"
                      :disabled="eligibilityPreviewLoading"
                      title="重新計算"
                      @click="previewManualEligibility"
                    >
                      <i class="ri-refresh-line" :class="{ spinning: eligibilityPreviewLoading }"></i>
                    </button>
                  </div>
                  <div class="eligibility-preview-body">
                    <div v-if="eligibilityPreviewLoading" class="eligibility-preview-loading">
                      <div class="spinner-small"></div>
                      <span>計算中...</span>
                    </div>
                    <div v-else-if="manualEligibilityPreviewError" class="eligibility-preview-error">
                      <i class="ri-error-warning-line"></i>
                      <span>{{ manualEligibilityPreviewError }}</span>
                    </div>
                    <div v-else-if="manualEligibilityPreviewCount != null" class="eligibility-preview-result">
                      <div class="eligibility-preview-count">
                        <span class="count-value">{{ manualEligibilityPreviewCount.toLocaleString() }}</span>
                        <span class="count-label">人</span>
                      </div>
                      <p class="eligibility-preview-hint">
                        <i class="ri-information-line"></i>
                        <span v-if="manualEligibilityPreviewUnrestricted">
                          已選全部等級且未設定標籤，所有會員皆符合參與對象條件
                        </span>
                        <span v-else>
                          符合目前標籤／等級條件的會員人數（與活動執行時判斷一致）
                        </span>
                      </p>
                    </div>
                    <div v-else class="eligibility-preview-placeholder">
                      <span>尚無預覽資料，請點上方重新整理計算</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </template>

            <template v-else>
              <div class="settings-readonly-block">
                <div class="readonly-row">
                  <strong>活動名稱：</strong>{{ campaign.campaignName }}
                </div>
                <div class="readonly-row">
                  <strong>期間：</strong>{{ formatPeriod(campaign.startAt, campaign.endAt) }}
                </div>
                <div v-if="campaign.budgetCap != null" class="readonly-row">
                  <strong>預算上限：</strong>{{ Number(campaign.budgetCap).toLocaleString() }} 元
                </div>
                <div v-if="eligibilityMode === 'rule' && selectedAudienceRule" class="readonly-row">
                  <strong>參與對象（受眾規則）：</strong>{{ selectedAudienceRule.name }}
                  <template v-if="selectedAudienceRule.audienceCount != null">
                    （約 {{ Number(selectedAudienceRule.audienceCount).toLocaleString() }} 人）
                  </template>
                </div>
                <div v-else-if="eligibilityMode === 'friends'" class="readonly-row">
                  <strong>參與對象：</strong>全部好友
                  <template v-if="eligibilityTagIds.length">
                    （標籤 {{ eligibilityTagLogic }}：{{ eligibilityTagIds.length }} 個）
                  </template>
                </div>
                <div v-else-if="eligibilityMode === 'members'">
                  <div class="readonly-row">
                    <strong>參與對象：</strong>會員限定
                  </div>
                  <div v-if="eligibilityTierLabels.length" class="readonly-row">
                    <strong>會員等級：</strong>{{ eligibilityTierLabels.join('、') }}
                  </div>
                  <div v-if="eligibilityTagIds.length" class="readonly-row">
                    <strong>標籤（{{ eligibilityTagLogic }}）：</strong>{{ eligibilityTagIds.length }} 個標籤
                  </div>
                </div>
                <div v-else class="readonly-row">
                  <strong>參與對象：</strong>全部好友
                </div>
                <p class="settings-readonly-note">
                  僅草稿狀態可編輯名稱、期間、參與對象與流程步驟；啟用或停用後如需大幅調整，建議建立新活動。
                </p>
              </div>
            </template>
          </div>
      </div>

      <div v-show="activeTab === 'flow'" class="edit-page-layout">
        <div class="edit-main-column">
          <div v-if="isEditable && campaignEnabled && validation && !validation.readyForReview" class="validation-box validation-box-inline">
            <h3>尚待完成項目</h3>
            <ul class="missing-list">
              <li v-for="(item, idx) in validation.missingFields" :key="idx">
                {{ item.message }}
              </li>
            </ul>
            <p class="validation-hint">請依上方提示補齊流程步驟，再按右上角「儲存」即可。</p>
          </div>

          <div v-if="validation?.warnings?.length" class="validation-box validation-box-warn validation-box-inline">
            <h3>注意事項</h3>
            <ul class="missing-list">
              <li v-for="(item, idx) in validation.warnings" :key="`warn-${idx}`">
                {{ item.message }}
              </li>
            </ul>
          </div>

          <div class="flow-guide">
            <button
              type="button"
              class="flow-guide-toggle"
              :aria-expanded="showFlowGuide"
              @click="showFlowGuide = !showFlowGuide"
            >
              <i class="ri-question-line"></i>
              <span>流程步驟與動作說明</span>
              <i class="flow-guide-chevron" :class="showFlowGuide ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
            </button>
            <div v-show="showFlowGuide" class="flow-guide-body">
              <p class="flow-guide-intro">
                會員進入活動後，會依序經過左側的<strong>步驟</strong>。每個步驟決定「何時觸發、誰符合、然後做什麼」；右上方<strong>儲存</strong>會一次儲存活動設定、流程步驟與啟用狀態（「設定」與「流程步驟」分頁的變更皆會一併儲存）。
              </p>
              <dl class="flow-guide-defs">
                <div class="flow-guide-def">
                  <dt>步驟</dt>
                  <dd>
                    流程中的一個關卡，依序執行（步驟 1 → 步驟 2 → …）。每步包含：
                    <ul>
                      <li><strong>【何時】觸發</strong>：例如加 LINE 好友、註冊、到指定時間、等待 N 天、消費滿額</li>
                      <li><strong>【誰符合】條件</strong>：選填，可再用標籤篩選要執行此步驟的會員</li>
                      <li><strong>【然後】動作</strong>：觸發後要執行的事（見右欄「動作」）</li>
                    </ul>
                  </dd>
                </div>
                <div class="flow-guide-def">
                  <dt>動作</dt>
                  <dd>
                    步驟觸發後實際執行的操作，同一步驟可新增多個、依序執行。例如：派發點數 → 發券 → 發 LINE 推播。
                    支援派點、派券、推播、抽獎連結、派送問卷、標記活動完成等。
                  </dd>
                </div>
                <div class="flow-guide-def">
                  <dt>參與對象 vs 步驟條件</dt>
                  <dd>
                    「設定」分頁的「參與對象」決定<strong>誰可以進入整個活動</strong>（全部好友、會員限定或受眾規則）；各步驟內的標籤篩選則是<strong>進入活動後</strong>，進一步限制誰執行該步驟。
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div class="journey-toolbar">
            <h2 class="section-heading">行銷活動流程步驟</h2>
            <div v-if="isEditable" class="journey-toolbar-actions">
              <button type="button" class="btn btn-outline-primary btn-sm" @click="addStep">
                <i class="ri-add-line"></i>
                新增步驟
              </button>
            </div>
          </div>
          <div class="journey-layout">
            <aside class="step-timeline">
              <button
                v-for="step in steps"
                :key="step.id || `local-${step.stepOrder}`"
                type="button"
                class="timeline-item"
                :class="{ active: (step.id || `local-${step.stepOrder}`) === selectedStepKey }"
                @click="selectStep(step)"
              >
                <span class="step-order">{{ step.stepOrder }}</span>
                <span class="step-name">{{ step.stepName }}</span>
              </button>
            </aside>

            <section v-if="selectedStep" class="step-editor">
              <div v-if="isEditable" class="step-editor-header">
                <div class="step-editor-header-info">
                  <span class="step-editor-badge">步驟 {{ selectedStepIndex + 1 }}</span>
                  <span class="step-editor-name">{{ selectedStep.stepName || '未命名步驟' }}</span>
                </div>
                <div class="step-editor-header-actions">
                  <div class="step-order-actions">
                    <span class="step-order-label">調整順序</span>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      :disabled="selectedStepIndex === 0"
                      title="上移步驟"
                      @click="moveStep(-1)"
                    >
                      <i class="ri-arrow-up-line"></i>
                      上移步驟
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      :disabled="selectedStepIndex >= steps.length - 1"
                      title="下移步驟"
                      @click="moveStep(1)"
                    >
                      <i class="ri-arrow-down-line"></i>
                      下移步驟
                    </button>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger step-delete-btn"
                    :disabled="steps.length <= 1"
                    title="刪除此步驟"
                    @click="removeStep"
                  >
                    <i class="ri-delete-bin-line"></i>
                    刪除步驟
                  </button>
                </div>
              </div>
              <MarketingCampaignStepEditor
                :step="selectedStep"
                :disabled="!isEditable"
                :coupon-templates="couponTemplates"
                :line-templates="lineTemplates"
                :lottery-configs="lotteryConfigs"
                :wixtar-tickets="wixtarTickets"
                :wixtar-tickets-loading="wixtarTicketsLoading"
                :participant-mode="eligibilityMode"
                :member-system-type="memberSystemType"
                @update:step="updateSelectedStep"
              />
            </section>
          </div>
        </div>

        <aside v-if="steps.length" class="edit-overview-column">
          <MarketingCampaignFlowOverview
            embedded
            :steps="steps"
            :coupon-templates="couponTemplates"
            :line-templates="lineTemplates"
            :lottery-configs="lotteryConfigs"
            :wixtar-tickets="wixtarTickets"
            :selected-step-key="selectedStepKey"
            :estimated-audience="flowOverviewEstimate?.estimatedAudience ?? null"
            :total-cost="flowOverviewEstimate?.cost?.totalCost ?? null"
            :cost-breakdown="flowOverviewEstimate?.cost ?? null"
            :audience-unrestricted="Boolean(flowOverviewEstimate?.unrestricted)"
            :estimate-loading="flowOverviewLoading"
            :estimate-error="flowOverviewError"
            @select="selectStepByKey"
          />
        </aside>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { marketingCampaignService } from '@/api/modules/marketingCampaigns.js';
import { couponTemplateService } from '@/api/modules/couponTemplates.js';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';
import { lotteryService } from '@/api/modules/lottery.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { audienceRuleService } from '@/api/modules/audienceRules.js';
import { clientService } from '@/api/modules/clients.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import authService from '@/helpers/authservice/authService.js';
import MarketingCampaignStepEditor from '@/components/marketing/MarketingCampaignStepEditor.vue';
import MarketingCampaignFlowOverview from '@/components/marketing/MarketingCampaignFlowOverview.vue';
import MarketingCampaignAnalyticsPanel from '@/components/marketing/MarketingCampaignAnalyticsPanel.vue';
import MarketingCampaignParticipantsPanel from '@/components/marketing/MarketingCampaignParticipantsPanel.vue';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import TagMatchTypeToggle from '@/components/TagMatchTypeToggle.vue';
import { showError, showSuccess } from '@/utils/swal.js';
import { getPlaybookLabel } from '@/constants/marketingCampaign.js';

function toDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fromDateStart(value) {
  if (!value) return null;
  return new Date(`${value}T00:00:00`).toISOString();
}

function fromDateEnd(value) {
  if (!value) return null;
  return new Date(`${value}T23:59:59.999`).toISOString();
}

export default {
  name: 'EditMarketingCampaign',
  components: {
    MarketingCampaignStepEditor,
    MarketingCampaignFlowOverview,
    MarketingCampaignAnalyticsPanel,
    MarketingCampaignParticipantsPanel,
    TagSelectorMultiple,
    TagMatchTypeToggle
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const loading = ref(true);
    const saving = ref(false);
    const error = ref(null);
    const campaign = ref(null);
    const steps = ref([]);
    const validation = ref(null);
    const detailTabs = [
      { key: 'settings', label: '設定', icon: 'ri-settings-3-line' },
      { key: 'flow', label: '流程步驟', icon: 'ri-route-line' },
      { key: 'analytics', label: '成效', icon: 'ri-bar-chart-box-line' },
      { key: 'participants', label: '參與者', icon: 'ri-group-line' }
    ];
    const resolveTab = (tab) => (['settings', 'flow', 'analytics', 'participants'].includes(tab) ? tab : 'settings');
    const activeTab = ref(resolveTab(route.query.tab));
    const selectedStepKey = ref(null);
    const couponTemplates = ref([]);
    const lineTemplates = ref([]);
    const lotteryConfigs = ref([]);
    const memberTiers = ref([]);
    const memberTiersLoading = ref(false);
    const campaignForm = ref({
      campaignName: '',
      startAtDate: '',
      endAtDate: '',
      budgetCap: null
    });
    const eligibilityTagIds = ref([]);
    const eligibilityTagLogic = ref('OR');
    const eligibilityMemberTierIds = ref([]);
    const eligibilityMode = ref('friends');
    const eligibilityAudienceRuleId = ref('');
    const memberSystemType = ref('LOCKLINE');
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);
    const audienceRules = ref([]);
    const audienceRulesLoading = ref(false);
    const manualEligibilityPreviewCount = ref(null);
    const manualEligibilityPreviewError = ref(null);
    const manualEligibilityPreviewUnrestricted = ref(false);
    const eligibilityPreviewLoading = ref(false);
    const flowOverviewEstimate = ref(null);
    const flowOverviewLoading = ref(false);
    const flowOverviewError = ref(null);
    let flowOverviewTimer = null;
    let eligibilityPreviewTimer = null;
    let eligibilityPreviewSeq = 0;
    let flowOverviewSeq = 0;
    const showFlowGuide = ref(false);
    const campaignEnabled = ref(false);

    const isEditable = computed(() => campaign.value?.status === 'draft');

    const canToggleStatus = computed(() =>
      ['draft', 'active', 'inactive'].includes(campaign.value?.status)
    );

    const canShowSaveButton = computed(() =>
      ['settings', 'flow'].includes(activeTab.value) && canToggleStatus.value
    );

    const selectedAudienceRule = computed(() =>
      audienceRules.value.find((rule) => rule.id === eligibilityAudienceRuleId.value) || null
    );

    const hasEligibilitySummary = computed(() => {
      if (isEditable.value) return false;
      if (eligibilityMode.value === 'rule') return Boolean(eligibilityAudienceRuleId.value);
      if (eligibilityMode.value === 'members') {
        return eligibilityMemberTierIds.value.length > 0 || eligibilityTagIds.value.length > 0;
      }
      if (eligibilityMode.value === 'friends') {
        return eligibilityTagIds.value.length > 0;
      }
      return false;
    });

    const eligibilityTierLabels = computed(() =>
      eligibilityMemberTierIds.value
        .map((id) => {
          const tier = memberTiers.value.find((t) => Number(t.id) === Number(id));
          if (tier) return tier.tierName;
          if (typeof id === 'string' && !/^\d+$/.test(id)) return id;
          return null;
        })
        .filter(Boolean)
    );

    const selectedStepIndex = computed(() =>
      steps.value.findIndex((s) => (s.id || `local-${s.stepOrder}`) === selectedStepKey.value)
    );

    const selectedStep = computed(() =>
      selectedStepIndex.value >= 0 ? steps.value[selectedStepIndex.value] : steps.value[0]
    );

    const allMemberTierIds = () => memberTiers.value.map((tier) => Number(tier.id));

    const selectAllMemberTiers = () => {
      const ids = allMemberTierIds();
      if (ids.length) {
        eligibilityMemberTierIds.value = ids;
      }
    };

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        if (res?.success && res.data?.clientData?.memberSystemType) {
          memberSystemType.value = res.data.clientData.memberSystemType === 'WIXTAR' ? 'WIXTAR' : 'LOCKLINE';
        }
      } catch {
        memberSystemType.value = 'LOCKLINE';
      }
      if (memberSystemType.value !== 'WIXTAR') return;
      wixtarTicketsLoading.value = true;
      try {
        wixtarTickets.value = await wixtarService.listAllTicketCatalog();
      } catch (err) {
        console.error('載入 Wixtar 票券失敗:', err);
        wixtarTickets.value = [];
      } finally {
        wixtarTicketsLoading.value = false;
      }
    };

    const loadMemberTiers = async () => {
      memberTiersLoading.value = true;
      try {
        const res = await memberTierService.getTiers({ isActive: true });
        const raw = res.data?.data ?? res.data;
        memberTiers.value = Array.isArray(raw) ? raw : raw?.items || [];
      } catch {
        memberTiers.value = [];
      } finally {
        memberTiersLoading.value = false;
      }
    };

    const normalizeMemberTierIds = (ids) => {
      if (!ids?.length) return [];
      return ids.map((id) => {
        if (typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id))) {
          return Number(id);
        }
        const tier = memberTiers.value.find((t) => t.tierCode === id);
        return tier?.id ?? id;
      });
    };

    const isTierSelected = (tierId) =>
      eligibilityMemberTierIds.value.some((id) => Number(id) === Number(tierId));

    const toggleEligibilityTier = (tierId) => {
      const numericId = Number(tierId);
      if (isTierSelected(numericId)) {
        eligibilityMemberTierIds.value = eligibilityMemberTierIds.value.filter(
          (id) => Number(id) !== numericId
        );
      } else {
        eligibilityMemberTierIds.value = [...eligibilityMemberTierIds.value, numericId];
      }
    };

    const previewManualEligibility = async () => {
      await refreshEligibilityPreview();
    };

    const unwrapApiPayload = (res) => {
      if (!res || typeof res !== 'object') return null;
      if (
        Object.prototype.hasOwnProperty.call(res, 'estimatedAudience')
        || Object.prototype.hasOwnProperty.call(res, 'estimatedCount')
        || Object.prototype.hasOwnProperty.call(res, 'unrestricted')
        || Object.prototype.hasOwnProperty.call(res, 'cost')
      ) {
        return res;
      }
      const inner = res.data;
      if (inner && typeof inner === 'object') {
        if (
          Object.prototype.hasOwnProperty.call(inner, 'estimatedAudience')
          || Object.prototype.hasOwnProperty.call(inner, 'estimatedCount')
          || Object.prototype.hasOwnProperty.call(inner, 'unrestricted')
          || Object.prototype.hasOwnProperty.call(inner, 'cost')
        ) {
          return inner;
        }
        if (inner.data && typeof inner.data === 'object') {
          return inner.data;
        }
      }
      return inner ?? null;
    };

    const applyEligibilityPreviewResult = (data) => {
      const audience = Number(data?.estimatedAudience ?? data?.estimatedCount);
      if (!Number.isFinite(audience)) return false;
      manualEligibilityPreviewCount.value = audience;
      manualEligibilityPreviewUnrestricted.value = Boolean(data?.unrestricted);
      return true;
    };

    const loadAudienceRules = async () => {
      audienceRulesLoading.value = true;
      try {
        const res = await audienceRuleService.getAudienceRules({ pageSize: 100, isActive: true });
        const raw = res.data?.data ?? res.data;
        audienceRules.value = raw?.items || raw || [];
      } catch {
        audienceRules.value = [];
      } finally {
        audienceRulesLoading.value = false;
      }
    };

    const buildEligibilityPayload = () => {
      const hasTags = eligibilityTagIds.value.length > 0;
      const tagPayload = hasTags
        ? { tagIds: eligibilityTagIds.value, tagLogic: eligibilityTagLogic.value }
        : {};

      if (eligibilityMode.value === 'friends') {
        return { mode: 'friends', ...tagPayload };
      }

      if (eligibilityMode.value === 'rule') {
        return eligibilityAudienceRuleId.value
          ? { mode: 'rule', audienceRuleId: eligibilityAudienceRuleId.value }
          : { mode: 'friends' };
      }

      return {
        mode: 'members',
        memberTierIds: eligibilityMemberTierIds.value.map(Number),
        ...tagPayload
      };
    };

    const mapLegacyEligibilityMode = (eligibility) => {
      if (!eligibility || typeof eligibility !== 'object') return 'friends';
      if (eligibility.mode === 'friends') return 'friends';
      if (eligibility.mode === 'members') return 'members';
      if (eligibility.mode === 'rule' || eligibility.audienceRuleId) return 'rule';
      if (eligibility.mode === 'manual') return 'members';
      if (eligibility.mode === 'all') return 'friends';
      if ((eligibility.memberTierIds?.length ?? 0) > 0) return 'members';
      return 'friends';
    };

    const loadResources = async () => {
      try {
        const [couponRes, lineRes, lotteryRes] = await Promise.all([
          couponTemplateService.getCouponTemplates({ pageSize: 100, isActive: true }),
          lineMsgTemplateService.list({ pageSize: 100, status: 'active' }),
          lotteryService.getConfigs({ pageSize: 100, isActive: true })
        ]);
        const couponData = couponRes.data?.data ?? couponRes.data;
        const lineData = lineRes.data?.data ?? lineRes.data;
        const lotteryData = lotteryRes.data?.data ?? lotteryRes.data;
        couponTemplates.value = couponData?.items || couponData || [];
        lineTemplates.value = lineData?.items || lineData || [];
        lotteryConfigs.value = lotteryData?.items || lotteryData || [];
      } catch {
        couponTemplates.value = [];
        lineTemplates.value = [];
        lotteryConfigs.value = [];
      }
    };

    const syncCampaignForm = (data) => {
      campaignForm.value = {
        campaignName: data.campaignName || '',
        startAtDate: toDateInput(data.startAt),
        endAtDate: toDateInput(data.endAt),
        budgetCap: data.budgetCap ?? null
      };

      const eligibility = data.eligibility;
      const mappedMode = mapLegacyEligibilityMode(eligibility);

      eligibilityMode.value = mappedMode;
      eligibilityAudienceRuleId.value = '';
      eligibilityTagIds.value = [];
      eligibilityTagLogic.value = 'OR';
      eligibilityMemberTierIds.value = [];

      if (mappedMode === 'rule') {
        eligibilityAudienceRuleId.value = eligibility?.audienceRuleId || '';
        return;
      }

      eligibilityTagIds.value = eligibility?.tagIds || [];
      eligibilityTagLogic.value = eligibility?.tagLogic || 'OR';

      if (mappedMode === 'members') {
        const tierIds = normalizeMemberTierIds(eligibility?.memberTierIds || []);
        eligibilityMemberTierIds.value = tierIds.length ? tierIds : allMemberTierIds();
        if (!eligibilityMemberTierIds.value.length) {
          selectAllMemberTiers();
        }
      }
    };

    const normalizeLegacyAction = (action) => {
      if (!action || typeof action !== 'object') return action;
      if (action.actionType === 'ACTION-009' && action.actionConfig?.markCompleted === true) {
        return {
          ...action,
          actionType: 'ACTION-011',
          actionConfig: {}
        };
      }
      return action;
    };

    const normalizeLoadedSteps = (rawSteps = []) =>
      (rawSteps || []).map((step) => ({
        ...step,
        actions: (step.actions || []).map(normalizeLegacyAction)
      }));

    const load = async () => {
      loading.value = true;
      error.value = null;
      try {
        const [detailRes, validateRes] = await Promise.all([
          marketingCampaignService.getCampaign(route.params.id),
          marketingCampaignService.validateCampaign(route.params.id)
        ]);
        const data = detailRes.data?.data ?? detailRes.data;
        campaign.value = data;
        steps.value = JSON.parse(JSON.stringify(normalizeLoadedSteps(data?.steps || [])));
        campaignEnabled.value = data?.status === 'active';
        const first = steps.value[0];
        selectedStepKey.value = first ? (first.id || `local-${first.stepOrder}`) : null;
        syncCampaignForm(data);
        validation.value = validateRes.data?.data ?? validateRes.data;

        if (
          data.eligibility?.audienceRuleId &&
          !audienceRules.value.some((rule) => rule.id === data.eligibility.audienceRuleId)
        ) {
          try {
            const ruleRes = await audienceRuleService.getAudienceRule(data.eligibility.audienceRuleId);
            const rule = ruleRes.data?.data ?? ruleRes.data;
            if (rule) {
              audienceRules.value = [...audienceRules.value, rule];
            }
          } catch {
            // ignore missing rule
          }
        }
      } catch (e) {
        error.value = e.response?.data?.message || e.message || '載入失敗';
      } finally {
        loading.value = false;
        if (campaign.value && ['friends', 'members'].includes(eligibilityMode.value)) {
          refreshEligibilityPreview();
        }
        if (campaign.value) {
          refreshFlowOverviewEstimate();
        }
      }
    };

    const selectStep = (step) => {
      selectedStepKey.value = step.id || `local-${step.stepOrder}`;
    };

    const selectStepByKey = (stepKey) => {
      selectedStepKey.value = stepKey;
    };

    const renumberSteps = (list) =>
      list.map((step, idx) => ({ ...step, stepOrder: idx + 1 }));

    const updateSelectedStep = (nextStep) => {
      if (selectedStepIndex.value < 0) return;
      const list = [...steps.value];
      list[selectedStepIndex.value] = nextStep;
      steps.value = list;
    };

    const addStep = () => {
      const order = steps.value.length + 1;
      const newStep = {
        id: null,
        stepOrder: order,
        stepName: `新步驟 ${order}`,
        triggerType: 'EVENT-201',
        triggerConfig: { runOnActivate: true },
        conditionGroup: null,
        actions: [{
          actionType: 'ACTION-008',
          executionOrder: 1,
          actionConfig: { channel: 'line' }
        }]
      };
      steps.value = [...steps.value, newStep];
      selectedStepKey.value = `local-${order}`;
    };

    const removeStep = () => {
      if (steps.value.length <= 1) return;
      const idx = selectedStepIndex.value;
      const list = steps.value.filter((_, i) => i !== idx);
      steps.value = renumberSteps(list);
      const first = steps.value[0];
      selectedStepKey.value = first ? (first.id || `local-${first.stepOrder}`) : null;
    };

    const moveStep = (delta) => {
      const idx = selectedStepIndex.value;
      const target = idx + delta;
      if (target < 0 || target >= steps.value.length) return;
      const list = [...steps.value];
      [list[idx], list[target]] = [list[target], list[idx]];
      steps.value = renumberSteps(list);
      const moved = steps.value[target];
      selectedStepKey.value = moved.id || `local-${moved.stepOrder}`;
    };

    const buildStepsPayload = () =>
      steps.value.map((step) => ({
        stepOrder: step.stepOrder,
        stepName: step.stepName,
        stepDescription: step.stepDescription || null,
        triggerType: step.triggerType,
        triggerConfig: step.triggerConfig || {},
        conditionGroup: step.conditionGroup || null,
        isRepeatable: step.isRepeatable || false,
        actions: (step.actions || []).map((action, idx) => ({
          actionType: action.actionType,
          actionConfig: action.actionConfig || {},
          executionOrder: idx + 1,
          isAsync: action.isAsync || false
        }))
      }));

    const buildFlowOverviewEstimatePayload = () => ({
      steps: buildStepsPayload(),
      eligibilityMode: eligibilityMode.value,
      eligibility: buildEligibilityPayload() || {},
      attributionConfig: campaign.value?.attributionConfig || {}
    });

    const refreshEligibilityPreview = async () => {
      if (!['friends', 'members'].includes(eligibilityMode.value)) {
        manualEligibilityPreviewCount.value = null;
        manualEligibilityPreviewUnrestricted.value = false;
        manualEligibilityPreviewError.value = null;
        return;
      }

      const seq = ++eligibilityPreviewSeq;
      eligibilityPreviewLoading.value = true;
      manualEligibilityPreviewError.value = null;

      try {
        const res = await marketingCampaignService.previewEligibility(buildEligibilityPayload());
        if (seq !== eligibilityPreviewSeq) return;

        const data = unwrapApiPayload(res);
        if (!applyEligibilityPreviewResult(data)) {
          manualEligibilityPreviewCount.value = null;
          manualEligibilityPreviewError.value = '預覽回應格式異常，請稍後再試';
        }
      } catch (e) {
        if (seq !== eligibilityPreviewSeq) return;
        const message = e.response?.data?.message || e.message || '預覽失敗';
        manualEligibilityPreviewError.value = message;
        manualEligibilityPreviewCount.value = null;
        manualEligibilityPreviewUnrestricted.value = false;
      } finally {
        if (seq === eligibilityPreviewSeq) {
          eligibilityPreviewLoading.value = false;
        }
      }
    };

    const refreshFlowOverviewEstimate = async () => {
      const seq = ++flowOverviewSeq;
      flowOverviewLoading.value = true;
      flowOverviewError.value = null;

      try {
        const res = await marketingCampaignService.estimateFlowOverview(buildFlowOverviewEstimatePayload());
        if (seq !== flowOverviewSeq) return;

        const data = unwrapApiPayload(res);
        flowOverviewEstimate.value = data;

        if (['friends', 'members'].includes(eligibilityMode.value)) {
          applyEligibilityPreviewResult(data);
        }
      } catch (e) {
        if (seq !== flowOverviewSeq) return;
        const message = e.response?.data?.message || e.message || '試算失敗';
        flowOverviewError.value = message;
        flowOverviewEstimate.value = null;
      } finally {
        if (seq === flowOverviewSeq) {
          flowOverviewLoading.value = false;
        }
      }
    };

    const scheduleEligibilityPreview = () => {
      if (!['friends', 'members'].includes(eligibilityMode.value)) {
        manualEligibilityPreviewCount.value = null;
        manualEligibilityPreviewUnrestricted.value = false;
        manualEligibilityPreviewError.value = null;
        return;
      }
      if (eligibilityPreviewTimer) {
        clearTimeout(eligibilityPreviewTimer);
      }
      eligibilityPreviewTimer = setTimeout(() => {
        refreshEligibilityPreview();
      }, 400);
    };

    const saveCampaignSettings = async () => {
      await marketingCampaignService.updateCampaign(route.params.id, {
        campaignName: campaignForm.value.campaignName,
        startAt: fromDateStart(campaignForm.value.startAtDate),
        endAt: fromDateEnd(campaignForm.value.endAtDate),
        budgetCap: campaignForm.value.budgetCap,
        eligibility: buildEligibilityPayload()
      });
    };

    const applyStatusChange = async (previousStatus, wantsActive) => {
      if (wantsActive && previousStatus !== 'active') {
        await marketingCampaignService.activateCampaign(route.params.id);
        return 'activated';
      }
      if (!wantsActive && previousStatus === 'active') {
        await marketingCampaignService.deactivateCampaign(route.params.id);
        return 'deactivated';
      }
      return null;
    };

    const handleSave = async () => {
      saving.value = true;
      try {
        const previousStatus = campaign.value?.status;
        const wantsActive = campaignEnabled.value;

        if (previousStatus === 'draft') {
          await saveCampaignSettings();
          await marketingCampaignService.saveCampaignSteps(route.params.id, buildStepsPayload());
        }

        const statusChange = await applyStatusChange(previousStatus, wantsActive);

        if (previousStatus !== 'draft' && !statusChange) {
          showSuccess('沒有變更需要儲存');
          return;
        }

        let message = '已儲存';
        if (previousStatus === 'draft') {
          message = statusChange === 'activated'
            ? '已儲存並啟用活動'
            : '已儲存活動設定與流程步驟';
        } else if (statusChange === 'deactivated') {
          message = '已停用活動';
        } else if (statusChange === 'activated') {
          message = '已啟用活動';
        }

        showSuccess(message);
        if (statusChange === 'activated') {
          activeTab.value = 'analytics';
        }
        await load();
      } catch (e) {
        campaignEnabled.value = campaign.value?.status === 'active';
        showError(e.response?.data?.message || e.message || '儲存失敗');
      } finally {
        saving.value = false;
      }
    };

    const formatPeriod = (start, end) => {
      const fmt = (v) => new Date(v).toLocaleString('zh-TW');
      return `${fmt(start)} ~ ${fmt(end)}`;
    };

    const statusLabel = (status) => ({
      draft: '草稿',
      active: '啟用',
      inactive: '停用',
      ended: '已結束'
    }[status] || status);

    watch(activeTab, (tab) => {
      const query = { ...route.query };
      if (tab === 'settings') {
        delete query.tab;
      } else {
        query.tab = tab;
      }
      router.replace({ query });
    });

    watch(eligibilityMode, (mode) => {
      if (mode === 'friends') {
        eligibilityAudienceRuleId.value = '';
        eligibilityMemberTierIds.value = [];
      } else if (mode === 'members') {
        eligibilityAudienceRuleId.value = '';
        if (!eligibilityMemberTierIds.value.length) {
          selectAllMemberTiers();
        }
      } else if (mode === 'rule') {
        eligibilityTagIds.value = [];
        eligibilityTagLogic.value = 'OR';
        eligibilityMemberTierIds.value = [];
        manualEligibilityPreviewCount.value = null;
        manualEligibilityPreviewUnrestricted.value = false;
        manualEligibilityPreviewError.value = null;
      }
      if (mode === 'friends' || mode === 'members') {
        scheduleEligibilityPreview();
      }
    });

    watch(
      [eligibilityMode, eligibilityTagIds, eligibilityTagLogic, eligibilityMemberTierIds],
      () => {
        scheduleEligibilityPreview();
      },
      { deep: true }
    );

    watch(
      steps,
      () => {
        if (flowOverviewTimer) {
          clearTimeout(flowOverviewTimer);
        }
        flowOverviewTimer = setTimeout(() => {
          refreshFlowOverviewEstimate();
        }, 400);
      },
      { deep: true }
    );

    onMounted(async () => {
      await Promise.all([loadResources(), loadMemberTiers(), loadAudienceRules(), loadClientMemberSystem()]);
      await load();
      if (eligibilityMode.value === 'members' && !eligibilityMemberTierIds.value.length) {
        selectAllMemberTiers();
      }
    });

    return {
      loading,
      saving,
      error,
      campaign,
      steps,
      validation,
      detailTabs,
      activeTab,
      selectedStepKey,
      selectedStep,
      selectedStepIndex,
      isEditable,
      campaignEnabled,
      canToggleStatus,
      canShowSaveButton,
      couponTemplates,
      lineTemplates,
      lotteryConfigs,
      campaignForm,
      eligibilityTagIds,
      eligibilityTagLogic,
      eligibilityMemberTierIds,
      eligibilityMode,
      eligibilityAudienceRuleId,
      memberSystemType,
      wixtarTickets,
      wixtarTicketsLoading,
      audienceRules,
      audienceRulesLoading,
      selectedAudienceRule,
      manualEligibilityPreviewCount,
      manualEligibilityPreviewError,
      manualEligibilityPreviewUnrestricted,
      eligibilityPreviewLoading,
      previewManualEligibility,
      flowOverviewEstimate,
      flowOverviewLoading,
      flowOverviewError,
      memberTiers,
      memberTiersLoading,
      hasEligibilitySummary,
      eligibilityTierLabels,
      isTierSelected,
      toggleEligibilityTier,
      selectStep,
      selectStepByKey,
      updateSelectedStep,
      addStep,
      removeStep,
      moveStep,
      handleSave,
      showFlowGuide,
      formatPeriod,
      statusLabel,
      playbookLabel: getPlaybookLabel
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
</style>

<style scoped>
.meta-card {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 8px 16px;
  padding: 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 16px;
}

.validation-box {
  padding: 12px 16px;
  background: #fff8e6;
  border: 1px solid #ffe08a;
  border-radius: 8px;
  margin-bottom: 16px;
}

.detail-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 16px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #888;
  cursor: pointer;
}

.tab-btn:hover {
  color: #007bff;
}

.tab-btn.active {
  color: #007bff;
  border-bottom-color: #007bff;
  font-weight: 600;
}

.tab-panel {
  margin-bottom: 16px;
}

.settings-card {
  padding: 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 16px;
}

.settings-card h3 {
  margin: 0 0 12px;
  font-size: 16px;
}

.status-switch-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  margin-bottom: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.status-switch-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.status-switch-label {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.status-switch-text small {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.settings-readonly-block {
  margin-top: 4px;
}

.settings-readonly-block .readonly-row {
  margin-bottom: 8px;
  font-size: 14px;
  color: #334155;
}

.settings-readonly-note {
  margin: 12px 0 0;
  padding: 10px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
}

.validation-box-inline {
  margin-bottom: 16px;
}

.validation-box-warn {
  background: #f0f7ff;
  border-color: #b8d4f0;
}

.validation-hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #6c757d;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;
}

.toggle-switch.disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-switch .slider {
  position: absolute;
  inset: 0;
  background: #dee2e6;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-switch .slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .slider {
  background: #198754;
}

.toggle-switch input:checked + .slider::before {
  transform: translateX(20px);
}

.toggle-switch input:disabled + .slider {
  background: #e9ecef;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.tier-filter-group {
  margin-top: 0;
}

.tier-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px 0 4px;
}

.tier-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 999px;
  border: 1.5px solid #d1d5db;
  background: #f9fafb;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}

.tier-chip:hover {
  border-color: #2563eb;
  background: #eff6ff;
  color: #2563eb;
}

.tier-chip--active {
  border-color: #2563eb;
  background: #2563eb;
  color: #fff;
}

.tier-chip--active:hover {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #fff;
}

.tier-chip-icon {
  font-size: 15px;
}

.settings-card-readonly .readonly-row {
  margin-bottom: 6px;
  font-size: 14px;
}

.eligibility-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.eligibility-section .section-title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 600;
}

.eligibility-section-desc {
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.5;
  color: #6c757d;
}

.eligibility-mode-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.eligibility-mode-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  min-height: 72px;
}

.eligibility-mode-card input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.eligibility-mode-card--selected {
  border-color: #2563eb;
  background: #eff6ff;
}

.eligibility-mode-icon {
  flex-shrink: 0;
  font-size: 22px;
  line-height: 1;
  color: #9ca3af;
  margin-top: 2px;
}

.eligibility-mode-card--selected .eligibility-mode-icon {
  color: #2563eb;
}

.eligibility-mode-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.eligibility-mode-text span {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  line-height: 1.35;
}

.eligibility-mode-text small {
  font-size: 12px;
  line-height: 1.45;
  color: #6b7280;
}

.eligibility-mode-action-hint {
  color: #92400e;
}

.eligibility-mode-panel {
  margin-top: 16px;
  padding: 16px 18px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.eligibility-panel-field {
  margin: 0;
}

.eligibility-manual-block + .eligibility-manual-block {
  padding-top: 16px;
  border-top: 1px dashed #e5e7eb;
}

.eligibility-info-box {
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.55;
  color: #374151;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.eligibility-info-box--muted {
  color: #6b7280;
  background: transparent;
  border: none;
  padding: 0;
}

.eligibility-all-hint {
  margin: 16px 0 0;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.55;
  color: #6b7280;
  background: #f8fafc;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
}

.eligibility-section .form-hint {
  margin: 0;
}

.eligibility-section .form-hint a,
.eligibility-info-box a {
  color: #0d6efd;
  text-decoration: none;
}

.eligibility-section .form-hint a:hover,
.eligibility-info-box a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .eligibility-mode-cards {
    grid-template-columns: 1fr;
  }
}

.eligibility-preview-card {
  margin-top: 4px;
  padding: 16px;
  background: #fff;
  border: 1px solid #dbeafe;
  border-radius: 10px;
}

.eligibility-preview-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.eligibility-preview-header i {
  font-size: 18px;
  color: #2563eb;
}

.eligibility-preview-header .btn {
  margin-left: auto;
  padding: 4px 10px;
}

.eligibility-preview-body {
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.eligibility-preview-loading,
.eligibility-preview-error,
.eligibility-preview-placeholder {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #6b7280;
}

.eligibility-preview-placeholder {
  justify-content: center;
  padding: 8px 0;
}

.eligibility-preview-error {
  color: #b45309;
}

.eligibility-preview-result {
  width: 100%;
  text-align: center;
}

.eligibility-preview-count {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
  margin-bottom: 8px;
}

.eligibility-preview-count .count-value {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.eligibility-preview-count .count-label {
  font-size: 16px;
  color: #6b7280;
}

.eligibility-preview-hint {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7280;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
}

.eligibility-preview-hint i {
  margin-top: 2px;
  flex-shrink: 0;
}

.spinner-small {
  width: 18px;
  height: 18px;
  border: 2px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinning {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.edit-page-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  align-items: start;
}

.edit-main-column {
  min-width: 0;
}

.edit-overview-column {
  align-self: start;
}

.form-hint {
  font-size: 13px;
  color: #6c757d;
  margin: 4px 0 0;
}

.journey-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  margin-top: 16px;
}

.flow-guide {
  margin-top: 16px;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #f8fbff;
  overflow: hidden;
}

.flow-guide-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: #1d4ed8;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.flow-guide-toggle:hover {
  background: #eff6ff;
}

.flow-guide-chevron {
  margin-left: auto;
  font-size: 18px;
}

.flow-guide-body {
  padding: 0 16px 16px;
  border-top: 1px solid #dbeafe;
}

.flow-guide-intro {
  margin: 12px 0 0;
  font-size: 14px;
  color: #334155;
  line-height: 1.6;
}

.flow-guide-defs {
  margin: 12px 0 0;
}

.flow-guide-def {
  margin-top: 12px;
}

.flow-guide-def dt {
  font-size: 13px;
  font-weight: 700;
  color: #1e40af;
  margin-bottom: 4px;
}

.flow-guide-def dd {
  margin: 0;
  font-size: 13px;
  color: #475569;
  line-height: 1.6;
}

.flow-guide-def ul {
  margin: 6px 0 0;
  padding-left: 18px;
}

.flow-guide-def li {
  margin-top: 4px;
}

.journey-toolbar .section-heading {
  margin: 0;
}

.journey-toolbar-actions {
  display: flex;
  gap: 8px;
}

.step-editor {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.step-editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.step-editor-header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.step-editor-badge {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
}

.step-editor-name {
  font-size: 15px;
  font-weight: 600;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.step-editor-header-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.step-order-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.step-order-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.step-delete-btn {
  margin-left: 4px;
  padding-left: 12px;
  border-left: 1px solid #fecaca;
}

.step-editor :deep(.step-editor-panel) {
  padding: 16px;
}

.missing-list {
  margin: 8px 0 0;
  padding-left: 20px;
}

.section-heading {
  font-size: 18px;
  margin: 0 0 12px;
}

.journey-layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 16px;
}

.step-timeline {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.timeline-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.timeline-item.active {
  border-color: #0d6efd;
  background: #eef4ff;
}

.step-order {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #0d6efd;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
}

.step-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
}

.step-section {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.step-section h4 {
  margin: 0 0 8px;
  font-size: 14px;
  color: #495057;
}

.action-item {
  padding: 8px 0;
  border-bottom: 1px dashed #eee;
}

pre {
  background: #f8f9fa;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  overflow-x: auto;
}

.editor-note {
  margin-top: 12px;
  color: #6c757d;
  font-size: 13px;
}

.text-success {
  color: #198754;
}

.text-muted {
  color: #6c757d;
}

@media (max-width: 1100px) {
  .edit-page-layout {
    grid-template-columns: 1fr;
  }

  .edit-overview-column {
    order: 2;
  }
}

@media (max-width: 768px) {
  .journey-layout {
    grid-template-columns: 1fr;
  }
}
</style>
